/**
 * Banners estáticos en `public/` por nombre base (sin extensión fija).
 * Mismos formatos que campañas Admin: vídeo MP4/WebM; imagen JPG/PNG/WebP; GIF.
 */

/** @typedef {'video'|'image'} PublicBannerKind */

/** @typedef {{ src: string, kind: PublicBannerKind, type: 'video'|'image' }} PublicBannerAsset */

const PROBE_ORDER = [
  { ext: 'mp4', kind: 'video' },
  { ext: 'webm', kind: 'video' },
  { ext: 'png', kind: 'image' },
  { ext: 'jpg', kind: 'image' },
  { ext: 'jpeg', kind: 'image' },
  { ext: 'webp', kind: 'image' },
  { ext: 'gif', kind: 'image' },
];

/**
 * Nombres base en `public/` (sin extensión) por slot. Se prueba cada alias en orden.
 * Preferido: `banner_lateral_*`; legacy: `fallback_lateral_*`.
 */
export const PUBLIC_BANNER_BASES_BY_SLOT = {
  hero_marketplace: ['banner_hero'],
  lateral_mercado_subastas: ['banner_lateral_mercado', 'fallback_lateral_mercado'],
  lateral_comunidad: ['banner_lateral_comunidad', 'fallback_lateral_comunidad'],
  lateral_noticias: ['banner_lateral_noticias', 'fallback_lateral_noticias'],
};

/** @deprecated Usar `PUBLIC_BANNER_BASES_BY_SLOT` */
export const PUBLIC_BANNER_BASE_BY_SLOT = Object.fromEntries(
  Object.entries(PUBLIC_BANNER_BASES_BY_SLOT).map(([slot, bases]) => [slot, bases[0]])
);

const resolveCache = new Map();
const resolveSlotCache = new Map();

function probeMedia(url, kind) {
  if (typeof document === 'undefined') return Promise.resolve(false);
  if (kind === 'video') {
    return new Promise(resolve => {
      const el = document.createElement('video');
      const done = ok => {
        el.removeAttribute('src');
        el.load();
        resolve(ok);
      };
      el.preload = 'metadata';
      el.muted = true;
      el.playsInline = true;
      el.onloadeddata = () => done(true);
      el.onerror = () => done(false);
      el.src = url;
    });
  }
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/**
 * Busca el primer `/{baseName}.{ext}` existente en `public/`.
 * @param {string} baseName — p. ej. `banner_hero`, `fallback_lateral_mercado`
 * @returns {Promise<PublicBannerAsset|null>}
 */
export async function resolvePublicBanner(baseName) {
  const base = String(baseName || '').trim().replace(/^\/+|\/+$/g, '');
  if (!base) return null;

  if (resolveCache.has(base)) return resolveCache.get(base);

  for (const { ext, kind } of PROBE_ORDER) {
    const src = `/${base}.${ext}`;
    // eslint-disable-next-line no-await-in-loop
    if (await probeMedia(src, kind)) {
      const asset = { src, kind, type: kind === 'video' ? 'video' : 'image' };
      resolveCache.set(base, asset);
      return asset;
    }
  }

  resolveCache.set(base, null);
  return null;
}

/**
 * @param {string} adSlot — valor de `ads.slot`
 * @returns {Promise<PublicBannerAsset|null>}
 */
export async function resolvePublicBannerForSlot(adSlot) {
  const key = String(adSlot || '').trim();
  if (!key) return null;
  if (resolveSlotCache.has(key)) return resolveSlotCache.get(key);

  const bases = PUBLIC_BANNER_BASES_BY_SLOT[key];
  if (!bases?.length) {
    resolveSlotCache.set(key, null);
    return null;
  }

  for (const base of bases) {
    // eslint-disable-next-line no-await-in-loop
    const asset = await resolvePublicBanner(base);
    if (asset) {
      resolveSlotCache.set(key, asset);
      return asset;
    }
  }

  resolveSlotCache.set(key, null);
  return null;
}

/**
 * Slide para carrusel hero por defecto (marketplace).
 * @param {PublicBannerAsset} asset
 */
export function heroSlideFromPublicBanner(asset) {
  if (!asset?.src) return null;
  return {
    id: 'banner-hero-static',
    type: asset.type,
    src: asset.src,
  };
}
